import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Pencil,
  Trash2,
  UserCog,
  Clock,
  CalendarDays,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field } from "@/components/forms";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OpsPersonCell } from "@/components/admin/ops-table";
import { formatDate } from "@/utils/formatters";
import {
  useAdminUnassigned,
  useUpdateUser,
  useDeleteUser,
} from "@/hooks/useAdminData";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { getFileUrl } from "@/lib/api-client";
import apiClient from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/admin/onboarding/")({
  component: AdminOnboardingPage,
});

function AdminOnboardingPage() {
  const [search, setSearch] = useState("");
  const [draftFilter, setDraftFilter] = useState("all");
  const queryClient = useQueryClient();

  const { data: unassignedData = [], isLoading } = useAdminUnassigned();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editData, setEditData] = useState({ name: "", email: "" });
  const [assignRole, setAssignRole] = useState("student");

  const filtered = unassignedData.filter((u: any) => {
    const name = (u.name || u.email || "").toLowerCase();
    const matchesSearch = name.includes(search.toLowerCase());
    const matchesDraft =
      draftFilter === "all" ||
      (draftFilter === "with_draft" && u.has_draft) ||
      (draftFilter === "no_draft" && !u.has_draft);
    return matchesSearch && matchesDraft;
  });

  const handleView = (u: any) => {
    setSelectedUser(u);
    setViewDialogOpen(true);
  };

  const handleEdit = (u: any) => {
    setSelectedUser(u);
    setEditData({ name: u.name || "", email: u.email || "" });
    setEditDialogOpen(true);
  };

  const handleDelete = (u: any) => {
    setSelectedUser(u);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedUser) return;
    deleteUser.mutate(selectedUser.id, {
      onSettled: () => setDeleteDialogOpen(false),
    });
  };

  const handleAssign = (u: any) => {
    setSelectedUser(u);
    setAssignRole("student");
    setAssignDialogOpen(true);
  };

  const confirmAssign = () => {
    if (!selectedUser) return;
    updateUser.mutate(
      {
        id: selectedUser.id,
        payload: {
          role: assignRole,
          status: "pending",
        },
      },
      {
        onSettled: () => {
          setAssignDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ["admin-unassigned"] });
        },
      }
    );
  };

  const handleSaveEdit = () => {
    if (!selectedUser) return;
    updateUser.mutate(
      {
        id: selectedUser.id,
        payload: {
          name: editData.name,
          email: editData.email,
        },
      },
      {
        onSettled: () => setEditDialogOpen(false),
      }
    );
  };


  return (
    <div className="space-y-6">
      <PageHeader
        title="Pending onboarding"
        subtitle="Users who have registered but have not yet identified themselves as a partner or student."
        breadcrumbs={[
          { label: "Dashboard", path: "/admin/dashboard" },
          { label: "Pending Onboarding" },
        ]}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Unassigned users" value={unassignedData.length} icon={<UserCog />} />
        <StatCard
          label="With onboarding draft"
          value={unassignedData.filter((u: any) => u.has_draft).length}
          icon={<Pencil />}
        />
        <StatCard
          label="No activity yet"
          value={unassignedData.filter((u: any) => !u.has_draft).length}
          icon={<Clock />}
        />
      </div>

      {/* Main table */}
      <SectionCard
        title="Registered users awaiting role assignment"
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name or email..."
              aria-label="Search by name or email"
            />
            <Select value={draftFilter} onValueChange={setDraftFilter}>
              <SelectTrigger className="h-8 w-full sm:w-[150px]" aria-label="Filter by draft">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                <SelectItem value="with_draft">With draft</SelectItem>
                <SelectItem value="no_draft">No draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={5} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon={<CheckCircle2 />}
            title="All clear"
            description="No users are pending onboarding at the moment."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Onboarding status</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead>Account status</TableHead>
                <TableHead className="pr-4 text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u: any) => (
                <TableRow key={u.id}>
                  <TableCell className="py-1.5 pl-4">
                    <OpsPersonCell
                      name={u.name || "Unnamed"}
                      initial={(u.name || u.email || "?").charAt(0).toUpperCase()}
                      avatarUrl={u.avatar_url ? getFileUrl(u.avatar_url) : undefined}
                    />
                  </TableCell>
                  <TableCell className="text-body">{u.email}</TableCell>
                  <TableCell>
                    {u.has_draft ? (
                      <Badge variant="info">{`Draft (${u.draft_status || "in progress"})`}</Badge>
                    ) : (
                      <Badge variant="muted">No draft started</Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {u.created_at ? formatDate(u.created_at) : "-"}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={u.status || "pending"} />
                  </TableCell>
                  <TableCell className="py-1.5 pr-3">
                    <div className="flex justify-end gap-0.5">
                      <IconAction
                        label="Assign role"
                        icon={<UserCog />}
                        onClick={() => handleAssign(u)}
                      />
                      <IconAction
                        label="View details"
                        icon={<ArrowRight />}
                        onClick={() => handleView(u)}
                      />
                      <IconAction label="Edit" icon={<Pencil />} onClick={() => handleEdit(u)} />
                      <IconAction
                        label="Delete"
                        tone="destructive"
                        icon={<Trash2 />}
                        onClick={() => handleDelete(u)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* View details dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(o) => !o && setViewDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User details</DialogTitle>
            <DialogDescription className="sr-only">
              Registration and onboarding details for this user.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="size-11 rounded-full">
                  {selectedUser.avatar_url ? (
                    <AvatarImage src={getFileUrl(selectedUser.avatar_url)} alt="" />
                  ) : null}
                  <AvatarFallback className="rounded-full text-sm">
                    {(selectedUser.name || selectedUser.email || "?").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-base font-medium text-foreground">
                    {selectedUser.name || "Unnamed"}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <Separator />

              <dl className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-border bg-card p-3">
                  <dt className="eyebrow mb-1.5">Account status</dt>
                  <dd>
                    <StatusChip status={selectedUser.status || "pending"} />
                  </dd>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <dt className="eyebrow mb-1.5">Verification</dt>
                  <dd>
                    <StatusChip status={selectedUser.verification_status || "unverified"} />
                  </dd>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <dt className="eyebrow mb-1.5 flex items-center gap-1">
                    <CalendarDays className="size-3" aria-hidden />
                    Registered
                  </dt>
                  <dd className="text-sm text-foreground tabular-nums">
                    {selectedUser.created_at ? formatDate(selectedUser.created_at) : "-"}
                  </dd>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <dt className="eyebrow mb-1.5 flex items-center gap-1">
                    <Clock className="size-3" aria-hidden />
                    Last login
                  </dt>
                  <dd className="text-sm text-foreground tabular-nums">
                    {selectedUser.last_login ? formatDate(selectedUser.last_login) : "Never"}
                  </dd>
                </div>
                <div className="col-span-2 rounded-md border border-border bg-card p-3">
                  <dt className="eyebrow mb-1.5">Onboarding draft</dt>
                  <dd>
                    {selectedUser.has_draft ? (
                      <Badge variant="info">
                        {`Started (${selectedUser.draft_status || "in progress"})`}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        No onboarding draft found
                      </span>
                    )}
                  </dd>
                </div>
                {selectedUser.draft_data && (
                  <div className="col-span-2 rounded-md border border-border bg-card p-3">
                    <dt className="eyebrow mb-1.5">Draft data</dt>
                    <dd>
                      <pre className="max-h-[200px] overflow-auto rounded-sm bg-background p-3 font-mono text-[11px] leading-4 break-all whitespace-pre-wrap text-foreground">
                        {(() => {
                          try {
                            return JSON.stringify(JSON.parse(selectedUser.draft_data), null, 2);
                          } catch {
                            return selectedUser.draft_data;
                          }
                        })()}
                      </pre>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedUser && (
              <Button
                onClick={() => {
                  setViewDialogOpen(false);
                  handleAssign(selectedUser);
                }}
              >
                <UserCog />
                Assign role
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign role dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(o) => !o && setAssignDialogOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign role</DialogTitle>
            <DialogDescription>
              Assign a role to{" "}
              <span className="font-medium text-foreground">
                {selectedUser?.name || selectedUser?.email}
              </span>
            </DialogDescription>
          </DialogHeader>
          <Field label="Select role">
            {({ id }) => (
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger id={id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignDialogOpen(false)}
              disabled={updateUser.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAssign}
              disabled={updateUser.isPending}
              loading={updateUser.isPending}
            >
              {updateUser.isPending ? "Assigning..." : "Assign role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription className="sr-only">Update this user's name and email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name">
              <Input
                value={editData.name}
                onChange={(e) => setEditData((d) => ({ ...d, name: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData((d) => ({ ...d, email: e.target.value }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateUser.isPending}
              loading={updateUser.isPending}
            >
              {updateUser.isPending ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <AlertTriangle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">
                {selectedUser?.name || selectedUser?.email}
              </span>
              ? This will permanently remove the account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteUser.isPending}
            >
              Keep active
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteUser.isPending}
              loading={deleteUser.isPending}
            >
              {!deleteUser.isPending && <AlertTriangle />}
              {deleteUser.isPending ? "Deleting..." : "Delete now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
